import { useState, useRef, useEffect, FormEvent } from 'react';

const eventTypes = [
  { id: 'birthday', label: 'Birthday Party', icon: 'ri-cake-2-line', baseRate: 18 },
  { id: 'wedding', label: 'Wedding', icon: 'ri-heart-line', baseRate: 28 },
  { id: 'corporate', label: 'Corporate Event', icon: 'ri-briefcase-line', baseRate: 22 },
  { id: 'cocktail', label: 'Cocktail Party', icon: 'ri-goblet-line', baseRate: 20 },
  { id: 'baby-shower', label: 'Baby Shower', icon: 'ri-star-smile-line', baseRate: 16 },
  { id: 'engagement', label: 'Engagement Party', icon: 'ri-sparkling-2-line', baseRate: 24 },
];

const addOnOptions = [
  { id: 'decor', label: 'Full Decor Styling', icon: 'ri-palette-line', price: 350 },
  { id: 'balloons', label: 'Balloon Installation', icon: 'ri-bubble-chart-line', price: 180 },
  { id: 'lighting', label: 'Ambient Lighting', icon: 'ri-lightbulb-line', price: 150 },
  { id: 'florals', label: 'Floral Arrangements', icon: 'ri-flower-line', price: 220 },
  { id: 'photo-booth', label: 'Photo Booth', icon: 'ri-camera-line', price: 300 },
  { id: 'dessert', label: 'Dessert Table', icon: 'ri-cake-3-line', price: 250 },
];

const guestRanges = [
  { label: '1–30', value: 25 },
  { label: '31–60', value: 50 },
  { label: '61–80', value: 75 },
  { label: '81–120', value: 100 },
  { label: '120+', value: 140 },
];

export default function QuoteEstimator() {
  const [visible, setVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const [guestRange, setGuestRange] = useState<number | null>(null);
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const selectedEventData = eventTypes.find((e) => e.id === selectedEvent);

  const baseEstimate = selectedEventData && guestRange
    ? selectedEventData.baseRate * guestRange
    : 0;

  const addOnsTotal = addOnOptions
    .filter((a) => selectedAddOns.includes(a.id))
    .reduce((sum, a) => sum + a.price, 0);

  const totalEstimate = baseEstimate + addOnsTotal;

  const toggleAddOn = (id: string) => {
    setSelectedAddOns((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const isReady = selectedEvent && guestRange !== null;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    setStatus('sending');
    try {
      const data = new FormData(form);
      const body = new URLSearchParams();
      data.forEach((val, key) => body.append(key, val as string));
      // Append estimator values
      body.append('event_type', selectedEventData?.label || '');
      body.append('guest_count', String(guestRange));
      body.append('selected_addons', addOnOptions.filter(a => selectedAddOns.includes(a.id)).map(a => a.label).join(', ') || 'None');
      body.append('estimated_quote', `$${totalEstimate.toLocaleString()}`);

      const res = await fetch('https://readdy.ai/api/form/d6kohktv117fnkj2gf5g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
    setTimeout(() => setStatus('idle'), 6000);
  };

  return (
    <section
      ref={ref}
      className={`py-24 px-6 lg:px-12 bg-gradient-to-b from-stone-50 to-white transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-amber-600 text-xs tracking-[0.35em] uppercase font-medium mb-2">
            Instant Estimate
          </p>
          <h2
            className="text-4xl font-light text-stone-800 mb-3"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Request a Quote
          </h2>
          <div className="w-14 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent mx-auto mb-5" />
          <p className="text-stone-500 text-sm max-w-xl mx-auto leading-relaxed">
            Get an instant price estimate for your event. Select your event type, guest count, and any add-ons — then send us your details for a formal quote.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
          {/* Left — Estimator Controls */}
          <div className="lg:col-span-3 space-y-8">

            {/* Step 1 — Event Type */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-400 text-stone-900 text-xs font-bold">1</div>
                <h3 className="text-stone-700 font-medium text-sm uppercase tracking-widest">Select Event Type</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {eventTypes.map((event) => (
                  <button
                    key={event.id}
                    type="button"
                    onClick={() => setSelectedEvent(event.id)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all cursor-pointer text-center ${
                      selectedEvent === event.id
                        ? 'border-amber-400 bg-amber-50 text-stone-800 shadow-sm shadow-amber-200'
                        : 'border-stone-200 bg-white text-stone-500 hover:border-amber-300 hover:bg-amber-50/50'
                    }`}
                  >
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className={`${event.icon} text-xl ${selectedEvent === event.id ? 'text-amber-500' : 'text-stone-400'}`}></i>
                    </div>
                    <span className="text-xs font-medium leading-tight">{event.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2 — Guest Count */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-400 text-stone-900 text-xs font-bold">2</div>
                <h3 className="text-stone-700 font-medium text-sm uppercase tracking-widest">Guest Count</h3>
              </div>
              <div className="flex flex-wrap gap-3">
                {guestRanges.map((range) => (
                  <button
                    key={range.value}
                    type="button"
                    onClick={() => setGuestRange(range.value)}
                    className={`px-5 py-2.5 rounded-full border-2 text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      guestRange === range.value
                        ? 'border-amber-400 bg-amber-400 text-stone-900 shadow-sm shadow-amber-200'
                        : 'border-stone-200 bg-white text-stone-500 hover:border-amber-300'
                    }`}
                  >
                    {range.label} guests
                  </button>
                ))}
              </div>
            </div>

            {/* Step 3 — Add-Ons */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-400 text-stone-900 text-xs font-bold">3</div>
                <h3 className="text-stone-700 font-medium text-sm uppercase tracking-widest">Add-Ons <span className="text-stone-400 normal-case font-normal">(optional)</span></h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {addOnOptions.map((addon) => (
                  <button
                    key={addon.id}
                    type="button"
                    onClick={() => toggleAddOn(addon.id)}
                    className={`flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all cursor-pointer text-left ${
                      selectedAddOns.includes(addon.id)
                        ? 'border-amber-400 bg-amber-50 text-stone-800'
                        : 'border-stone-200 bg-white text-stone-500 hover:border-amber-300 hover:bg-amber-50/40'
                    }`}
                  >
                    <div className="w-6 h-6 flex items-center justify-center shrink-0">
                      <i className={`${addon.icon} text-base ${selectedAddOns.includes(addon.id) ? 'text-amber-500' : 'text-stone-400'}`}></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium leading-tight truncate">{addon.label}</p>
                      <p className="text-xs text-amber-600 font-semibold mt-0.5">+${addon.price}</p>
                    </div>
                    {selectedAddOns.includes(addon.id) && (
                      <div className="w-4 h-4 flex items-center justify-center ml-auto shrink-0">
                        <i className="ri-checkbox-circle-fill text-amber-500 text-base"></i>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right — Estimate Summary */}
          <div className="lg:col-span-2 sticky top-24">
            <div className="bg-white rounded-2xl border border-stone-100 shadow-lg shadow-stone-100 overflow-hidden">
              {/* Summary Header */}
              <div className="bg-gradient-to-br from-stone-800 to-stone-900 px-6 py-5">
                <p className="text-amber-400 text-xs tracking-widest uppercase font-medium mb-1">Your Estimate</p>
                <div className="flex items-end gap-2">
                  <span
                    className="text-4xl font-light text-white"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {isReady ? `$${totalEstimate.toLocaleString()}` : '$—'}
                  </span>
                  {isReady && <span className="text-stone-400 text-sm mb-1">approx.</span>}
                </div>
                {!isReady && (
                  <p className="text-stone-400 text-xs mt-1">Select event type &amp; guest count to see estimate</p>
                )}
              </div>

              {/* Breakdown */}
              <div className="px-6 py-5 space-y-3">
                {selectedEventData && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-stone-500 flex items-center gap-2">
                      <i className={`${selectedEventData.icon} text-amber-400`}></i>
                      {selectedEventData.label}
                    </span>
                    <span className="text-stone-700 font-medium">${baseEstimate.toLocaleString()}</span>
                  </div>
                )}
                {addOnOptions.filter(a => selectedAddOns.includes(a.id)).map((addon) => (
                  <div key={addon.id} className="flex justify-between items-center text-sm">
                    <span className="text-stone-500 flex items-center gap-2">
                      <i className={`${addon.icon} text-amber-400`}></i>
                      {addon.label}
                    </span>
                    <span className="text-stone-700 font-medium">+${addon.price}</span>
                  </div>
                ))}
                {isReady && (
                  <>
                    <div className="border-t border-stone-100 pt-3 flex justify-between items-center">
                      <span className="text-stone-700 font-semibold text-sm">Total Estimate</span>
                      <span className="text-amber-600 font-bold text-base">${totalEstimate.toLocaleString()}</span>
                    </div>
                    <p className="text-stone-400 text-xs leading-relaxed">
                      This is an indicative estimate only. Final pricing depends on venue, specific requirements, and availability.
                    </p>
                  </>
                )}

                {/* CTA */}
                <button
                  type="button"
                  disabled={!isReady}
                  onClick={() => setShowForm(true)}
                  className="w-full mt-2 py-3 bg-gradient-to-r from-amber-400 to-amber-500 text-stone-900 text-sm font-semibold rounded-lg hover:from-amber-300 hover:to-amber-400 transition-all cursor-pointer whitespace-nowrap shadow-md shadow-amber-200 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isReady ? 'Request Formal Quote' : 'Complete Steps Above'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Quote Request Form — slides in */}
        {showForm && (
          <div className="mt-12 bg-white rounded-2xl border border-stone-100 shadow-lg shadow-stone-100 p-8 max-w-2xl mx-auto animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3
                  className="text-2xl font-light text-stone-800"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Confirm Your Quote Request
                </h3>
                <p className="text-stone-400 text-sm mt-1">
                  Estimated total: <span className="text-amber-600 font-semibold">${totalEstimate.toLocaleString()}</span> — we'll confirm within 24 hours.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-100 hover:bg-stone-200 text-stone-500 cursor-pointer transition"
              >
                <i className="ri-close-line text-base"></i>
              </button>
            </div>

            <form id="quote-request-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-stone-500 uppercase tracking-wider mb-1.5">First Name *</label>
                  <input
                    type="text"
                    name="first_name"
                    required
                    placeholder="Bella"
                    className="w-full px-4 py-3 rounded-lg border border-stone-200 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
                  />
                </div>
                <div>
                  <label className="block text-xs text-stone-500 uppercase tracking-wider mb-1.5">Last Name *</label>
                  <input
                    type="text"
                    name="last_name"
                    required
                    placeholder="Rose"
                    className="w-full px-4 py-3 rounded-lg border border-stone-200 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-stone-500 uppercase tracking-wider mb-1.5">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="hello@example.com"
                  className="w-full px-4 py-3 rounded-lg border border-stone-200 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
                />
              </div>

              <div>
                <label className="block text-xs text-stone-500 uppercase tracking-wider mb-1.5">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="04XX XXX XXX"
                  className="w-full px-4 py-3 rounded-lg border border-stone-200 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
                />
              </div>

              <div>
                <label className="block text-xs text-stone-500 uppercase tracking-wider mb-1.5">Event Date *</label>
                <input
                  type="date"
                  name="event_date"
                  required
                  className="w-full px-4 py-3 rounded-lg border border-stone-200 text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
                />
              </div>

              <div>
                <label className="block text-xs text-stone-500 uppercase tracking-wider mb-1.5">
                  Additional Notes <span className="normal-case text-stone-400">(max 500 characters)</span>
                </label>
                <textarea
                  name="notes"
                  maxLength={500}
                  rows={3}
                  placeholder="Any specific requirements, venue details, or questions..."
                  className="w-full px-4 py-3 rounded-lg border border-stone-200 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition resize-none"
                />
              </div>

              {/* Summary pill */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-stone-600 flex flex-wrap gap-2">
                <span className="font-semibold text-stone-700">Your selection:</span>
                <span className="bg-white border border-amber-200 rounded-full px-2.5 py-0.5">{selectedEventData?.label}</span>
                <span className="bg-white border border-amber-200 rounded-full px-2.5 py-0.5">{guestRange} guests</span>
                {addOnOptions.filter(a => selectedAddOns.includes(a.id)).map(a => (
                  <span key={a.id} className="bg-white border border-amber-200 rounded-full px-2.5 py-0.5">{a.label}</span>
                ))}
                <span className="ml-auto font-bold text-amber-600">${totalEstimate.toLocaleString()} est.</span>
              </div>

              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full py-3.5 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-stone-900 text-sm font-semibold rounded-lg hover:from-amber-300 hover:to-amber-500 transition-all cursor-pointer whitespace-nowrap shadow-md shadow-amber-400/30 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === 'sending' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i> Sending...
                  </span>
                ) : 'Submit Quote Request'}
              </button>

              {status === 'success' && (
                <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm">
                  <i className="ri-checkbox-circle-line text-base"></i>
                  Quote request sent! We'll be in touch within 24 hours.
                </div>
              )}
              {status === 'error' && (
                <div className="flex items-center gap-2 text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-4 py-3 text-sm">
                  <i className="ri-error-warning-line text-base"></i>
                  Something went wrong. Please try again.
                </div>
              )}
            </form>
          </div>
        )}
      </div>
    </section>
  );
}
